import { useState, useEffect, useCallback } from "react";
import { distribuicaoService } from "../../../services/api";

/**
 * Hook personalizado para gerenciar a lógica de negócios relacionada às distribuições
 */
export const useDistribuicoes = () => {
  // Estados para lista de distribuições e carregamento
  const [distribuicoes, setDistribuicoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("Todos");
  
  // Estados para filtros de data
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [numeroProcesso, setNumeroProcesso] = useState("");
  const [numeroProcessoDebounced, setNumeroProcessoDebounced] = useState("");

  // Estados para seleção em lote
  const [selectedItems, setSelectedItems] = useState([]);
  const [confirmBatchDelete, setConfirmBatchDelete] = useState(false);
  const [batchDeleteProgress, setBatchDeleteProgress] = useState({
    isDeleting: false,
    total: 0,
    completed: 0,
    success: 0,
    failed: 0,
  });

  // Buscar distribuições
  const fetchDistribuicoes = useCallback(async () => {
    try {
      setLoading(true);
      // Passa os parâmetros de filtro para a API
      const data = await distribuicaoService.buscarDistribuicao(
        selectedStatus,
        dataInicio,
        dataFim,
        numeroProcessoDebounced
      );
      
      // Não é mais necessário filtrar por status aqui, pois a API já retorna filtrado
      setDistribuicoes(data);
      setLoading(false);
      // Limpar seleções quando os dados são atualizados
      setSelectedItems([]);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      setError(error.message);
      setLoading(false);
    }
  }, [selectedStatus, dataInicio, dataFim, numeroProcessoDebounced]);

  // Buscar opções de status disponíveis
  const fetchStatusOptions = async () => {
    try {
      const data = await distribuicaoService.buscarDistribuicaoStatus();
      // Adiciona a opção "Todos" no início
      const options = [{ key: "Todos", value: "Todos" }];
      
      // Adiciona os demais status
      // Importante: usar o valor (value) como key para garantir que o status seja passado corretamente
      Object.entries(data).forEach(([key, value]) => {
        options.push({ key: value, value });
      });
      
      setStatusOptions(options);
    } catch (error) {
      console.error("Erro ao buscar status:", error);
      // Define opções padrão em caso de erro
      setStatusOptions([
        { key: "Todos", value: "Todos" },
        { key: "Pendente", value: "Pendente" },
        { key: "Finalizado", value: "Finalizado" }
      ]);
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    fetchStatusOptions();
    fetchDistribuicoes();
  }, [fetchDistribuicoes]);

  // Alterar status selecionado
  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setSelectedStatus(newStatus);
  };

  // Alterar data de início
  const handleDataInicioChange = (e) => {
    const newDataInicio = e.target.value;
    setDataInicio(newDataInicio);
  };

  // Alterar data de fim
  const handleDataFimChange = (e) => {
    const newDataFim = e.target.value;
    setDataFim(newDataFim);
  };

  // Alterar número do processo
  const handleNumeroProcessoChange = (e) => {
    const newNumeroProcesso = e.target.value;
    setNumeroProcesso(newNumeroProcesso);
  };

  // Debounce para o número do processo (aguarda 2 segundos após parar de digitar)
  useEffect(() => {
    const timer = setTimeout(() => {
      setNumeroProcessoDebounced(numeroProcesso);
    }, 2000);

    return () => {
      clearTimeout(timer);
    };
  }, [numeroProcesso]);

  // Efeito para refiltrar os dados quando o status, data ou processo muda
  useEffect(() => {
    fetchDistribuicoes();
  }, [selectedStatus, dataInicio, dataFim, numeroProcessoDebounced, fetchDistribuicoes]);

  // Excluir distribuição individual
  const excluirDistribuicao = async (id) => {
    try {
      // Implementar chamada para excluir quando disponível
      // await distribuicaoService.cancelarDistribuicao(id);
      
      // Por enquanto, apenas remove do estado local
      setDistribuicoes(prev => prev.filter(item => item.id !== id));
      return true;
    } catch (error) {
      console.error("Erro ao excluir distribuição:", error);
      return false;
    }
  };

  // Funções para seleção em lote
  const handleSelectItem = (id) => {
    setSelectedItems(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.length === distribuicoes.length) {
      // Se todos já estão selecionados, desmarca todos
      setSelectedItems([]);
    } else {
      // Seleciona todos
      setSelectedItems(distribuicoes.map(item => item.id));
    }
  };

  // Exclusão em lote
  const iniciarExclusaoEmLote = () => {
    if (selectedItems.length > 0) {
      setConfirmBatchDelete(true);
    }
  };

  const cancelarExclusaoEmLote = () => {
    setConfirmBatchDelete(false);
    // Reseta o progresso se estiver em andamento
    if (batchDeleteProgress.isDeleting) {
      setBatchDeleteProgress({
        isDeleting: false,
        total: 0,
        completed: 0,
        success: 0,
        failed: 0
      });
    }
  };

  const confirmarExclusaoEmLote = async () => {
    try {
      // Inicializa o progresso
      setBatchDeleteProgress({
        isDeleting: true,
        total: selectedItems.length,
        completed: 0,
        success: 0,
        failed: 0
      });

      // Executa as exclusões em sequência
      for (let i = 0; i < selectedItems.length; i++) {
        const id = selectedItems[i];
        try {
          // Implementar chamada para excluir quando disponível
          // await distribuicaoService.cancelarDistribuicao(id);
          
          // Por enquanto, apenas remove do estado local
          setDistribuicoes(prev => prev.filter(item => item.id !== id));
          
          // Atualiza o progresso de sucesso
          setBatchDeleteProgress(prev => ({
            ...prev,
            completed: i + 1,
            success: prev.success + 1
          }));
        } catch (error) {
          console.error(`Erro ao excluir distribuição ID ${id}:`, error);
          // Atualiza o progresso de falha
          setBatchDeleteProgress(prev => ({
            ...prev,
            completed: i + 1,
            failed: prev.failed + 1
          }));
        }
        
        // Pequena pausa para permitir a atualização da UI
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Finaliza o processo após todas as exclusões
      setTimeout(() => {
        // Atualiza a lista após excluir todos
        fetchDistribuicoes();
        setConfirmBatchDelete(false);
        setSelectedItems([]);
        setBatchDeleteProgress({
          isDeleting: false,
          total: 0,
          completed: 0,
          success: 0,
          failed: 0
        });
      }, 500);
    } catch (error) {
      console.error("Erro ao excluir distribuições em lote:", error);
      setBatchDeleteProgress(prev => ({
        ...prev,
        isDeleting: false
      }));
    }
  };

  return {
    // Dados
    distribuicoes,
    loading,
    error,
    statusOptions,
    selectedStatus,
    dataInicio,
    dataFim,
    numeroProcesso,
    selectedItems,
    confirmBatchDelete,
    batchDeleteProgress,
    
    // Ações
    fetchDistribuicoes,
    handleStatusChange,
    handleDataInicioChange,
    handleDataFimChange,
    handleNumeroProcessoChange,
    excluirDistribuicao,
    handleSelectItem,
    handleSelectAll,
    iniciarExclusaoEmLote,
    cancelarExclusaoEmLote,
    confirmarExclusaoEmLote,
  };
};
